import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Layers, Sparkles } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { FLASHCARD_CATEGORIES, totalFlashcardCount } from "@/data/flashcards";

/* Flashcards hub — mirrors the /sections aesthetic (light bg, 6 tile grid
   with accent-coloured icon squares) so the mental model is consistent.
   No auth required — free for everyone. */
export default function FlashcardsHub() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <header className="border-b border-slate-200 bg-white/70 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/sections" className="flex items-center gap-3" data-testid="brand-logo-home">
            <BrandLogo chip />
            <span className="hidden sm:block text-[11px] text-slate-500 border-l border-slate-200 pl-3">
              Flashcards
            </span>
          </Link>
          <Link
            to="/sections"
            data-testid="flashcards-hub-back"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#0047AB]"
          >
            <ArrowLeft className="w-4 h-4" /> Back to simulators
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12" data-testid="flashcards-hub-page">
        <div className="flex items-start gap-3 mb-3">
          <div className="mt-1 w-10 h-10 rounded-lg bg-[#0047AB] text-white flex items-center justify-center shrink-0">
            <Layers className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0047AB]">Study mode</p>
            <h1 className="mt-1 text-3xl sm:text-4xl font-bold text-slate-900">Flashcards</h1>
          </div>
        </div>
        <p className="text-base text-slate-600 max-w-2xl">
          Bite-sized study cards for the same six clinical areas your simulators cover. Flip, mark
          <span className="mx-1 inline-flex items-center gap-1 text-emerald-700 font-semibold">
            <Sparkles className="w-3.5 h-3.5" /> Got it
          </span>
          for the ones you know, keep the rest in rotation until they stick.
        </p>
        <p className="mt-1.5 text-sm text-slate-500">
          {totalFlashcardCount} cards across {FLASHCARD_CATEGORIES.length} categories · free for everyone · no login required
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {FLASHCARD_CATEGORIES.map((c) => {
            const Icon = c.Icon;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => navigate(`/flashcards/${c.id}`)}
                data-testid={`flashcards-tile-${c.id}`}
                className="group text-left bg-white border border-slate-200 rounded-xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
                style={{ borderTopWidth: 3, borderTopColor: c.accent }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center text-white shrink-0"
                    style={{ background: c.accent }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <span
                    className="text-2xl font-bold tabular-nums"
                    style={{ color: c.accent }}
                  >
                    {String(c.n).padStart(2, "0")}
                  </span>
                </div>

                <h2 className="text-lg font-semibold text-slate-900">{c.title}</h2>
                <p className="text-sm text-slate-600 leading-relaxed mt-1.5 flex-1">{c.blurb}</p>

                <div className="flex items-center justify-between mt-4">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    <Layers className="w-3.5 h-3.5" />
                    {c.cards.length} cards
                  </span>
                  <span
                    className="text-sm font-semibold inline-flex items-center gap-1 group-hover:gap-1.5 transition-all"
                    style={{ color: c.accent }}
                  >
                    Study <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
}
